// BuilderPieceDetail - ビルダー編集中の「詳しく」モーダル（専用・新規）
// ピースビルダートップの詳細ページと同じ見た目を踏襲しつつ、編集用に作り直したもの。
// 差分: 作成者なし / コメントは閲覧のみ / 名前・説明・ヒントは各エリアの「へんこう」ボタン→
//       専用編集Modalで個別に確定。統計・ランキング・コマンド・公開日時はトップと同様に表示。
// 全画面ではなく、トップ画面と同じ専用ダイアログ（InScreenModal + BuilderModalPanel）で表示する。
// 共有の MapDetailPage は流用せず、本ファイルで自己完結させる（leaf の CommandPanel のみ再利用）。

import { toast } from "react-toastify";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { AppDispatch, RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { UserAvatar } from "common/elements";
import { usePlaySound, btnDelay } from "common/utils";
import { HELP_DETAIL } from "features/builder/helpPages";
import { recordEditHistory } from "features/builder/hook";
import { useBuilderContext } from "features/builder/context";
import { actions, selectMapById } from "features/builder/slice";
import { ApiPieceComment, PieceRankingEntry } from "features/builder/types";
import { MapDetailCommandPanel } from "features/builder/elements/MapDetailCommandPanel";
import { IconBad, IconFun, IconCool, IconAmazing, IconDifficult } from "features/reaction/asset";
import {
  getComments,
  getPieceRanking,
  updatePieceMeta,
  updatePieceHints,
} from "features/builder/api";
import {
  PillSize,
  PillColor,
  InScreenModal,
  BuilderModalPanel,
  BuilderPillButton,
  BuilderModalAccent,
} from "features/builder/elements";
import { BuilderHelpPopup } from "./BuilderHelpPopup";
import { StockSelectModal } from "./StockSelectModal";

enum DetailTab {
  INFO = "info",
  COMMAND = "command",
  RANKING = "ranking",
  COMMENT = "comment",
}

enum EditField {
  NAME = "name",
  DESC = "desc",
  HINTS = "hints",
}

const TAB_ITEMS: { id: DetailTab; labelKey: string; icon: string }[] = [
  { id: DetailTab.INFO, labelKey: "MSG_BUILDER_MAP_DETAIL_BTN", icon: "info" },
  { id: DetailTab.COMMAND, labelKey: "MSG_BUILDER_DETAIL_TAB_COMMAND", icon: "extension" },
  { id: DetailTab.RANKING, labelKey: "MSG_BUILDER_DETAIL_TAB_RANKING", icon: "leaderboard" },
  { id: DetailTab.COMMENT, labelKey: "MSG_BUILDER_DETAIL_TAB_COMMENT", icon: "chat_bubble" },
];

const HINT_LEVELS = [
  "MSG_BUILDER_DETAIL_HINT_LV1",
  "MSG_BUILDER_DETAIL_HINT_LV2",
  "MSG_BUILDER_DETAIL_HINT_LV3",
];

const DESC_MIN = 10;

// 画像が無いときの既定表示（MapCard / MapDetailPage と同じ）
const FALLBACK_IMAGE = `${Constants.assetHost}/assets/images/builder_default_display.webp`;

// 1〜3位のメダル
const RANK_MEDALS = ["👑", "🥈", "🥉"];

const REACTIONS: { key: string; icon: JSX.Element }[] = [
  { key: "difficult", icon: <IconDifficult width={40} height={40} /> },
  {
    key: "easy",
    icon: (
      <img
        alt="*"
        draggable={false}
        className="w-10 h-10"
        src={`${Constants.assetHost}/assets/images/reaction_easy.png`}
      />
    ),
  },
  { key: "cool", icon: <IconCool width={40} height={40} /> },
  { key: "fun", icon: <IconFun width={40} height={40} /> },
  { key: "amazing", icon: <IconAmazing width={40} height={40} /> },
  { key: "bad", icon: <IconBad width={40} height={40} /> },
];

export const BuilderPieceDetail = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const play = usePlaySound();
  const dispatch = useDispatch<AppDispatch>();

  const name = useSelector((state: RootState) => state.builder.name);
  const description = useSelector((state: RootState) => state.builder.description);
  const hint = useSelector((state: RootState) => state.builder.hint);
  const enabledCommands = useSelector((state: RootState) => state.builder.enabledCommands);
  const commandLimits = useSelector((state: RootState) => state.builder.commandLimits);
  const pieceId = useSelector((state: RootState) => state.builder.editingBuilderMapId);
  const active = useSelector((state: RootState) => state.user.active);
  const background = useSelector((state: RootState) => state.builder.background);
  const cover = useSelector((state: RootState) => state.builder.cover);
  const editingMap = useSelector((state: RootState) => selectMapById(state, pieceId));
  const { triggerReload } = useBuilderContext();

  const [tab, setTab] = useState<DetailTab>(DetailTab.INFO);
  const [editing, setEditing] = useState<EditField | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  // 持ち物Modal: cover/background のどちらに適用するか（null = 非表示）
  const [stockTarget, setStockTarget] = useState<"cover" | "background" | null>(null);
  const [editName, setEditName] = useState(name);
  const [editDesc, setEditDesc] = useState(description);
  const [editHints, setEditHints] = useState<string[]>(hint);
  const [comments, setComments] = useState<ApiPieceComment[]>([]);
  const [ranking, setRanking] = useState<PieceRankingEntry[]>([]);

  // コメントはタブを開いたときに取得する（未公開ピースでは 0 件）
  useEffect(() => {
    if (!open || tab !== DetailTab.COMMENT || !pieceId) return;
    let canceled = false;
    getComments({ piece_id: pieceId, uid: active?.uid, limit: 20 })
      .then((rows) => {
        if (!canceled) setComments(rows);
      })
      .catch(() => {
        if (!canceled) setComments([]);
      });
    return () => {
      canceled = true;
    };
  }, [open, tab, pieceId, active?.uid]);

  // ランキングもタブを開いたときに取得する（クリア記録が無ければ 0 件）
  useEffect(() => {
    if (!open || tab !== DetailTab.RANKING || !pieceId) return;
    let canceled = false;
    getPieceRanking({ piece_id: pieceId, limit: 3 })
      .then((rows) => {
        if (!canceled) setRanking(rows);
      })
      .catch(() => {
        if (!canceled) setRanking([]);
      });
    return () => {
      canceled = true;
    };
  }, [open, tab, pieceId]);

  // 開くたびに redux 値で初期化（とじる＝キャンセル）
  useEffect(() => {
    if (open) {
      setTab(DetailTab.INFO);
      setEditing(null);
      setStockTarget(null);
      setEditName(name);
      setEditDesc(description);
      setEditHints([hint[0] ?? "", hint[1] ?? "", hint[2] ?? ""]);
    }
  }, [open, name, description, hint]);

  const descRemaining = Math.max(0, DESC_MIN - editDesc.trim().length);

  const handleBack = () => {
    play();
    btnDelay(() => onClose());
  };

  const handleSelectTab = (id: DetailTab) => () => {
    if (id === tab) return;
    play();
    btnDelay(() => setTab(id));
  };

  // カバー / はいけい変更: 持ち物（ストック）Modal を開く
  const handleChangeCover = () => {
    play();
    btnDelay(() => setStockTarget("cover"));
  };

  const handleChangeBackground = () => {
    play();
    btnDelay(() => setStockTarget("background"));
  };

  // 持ち物から選んだ画像を cover / background に適用して保存
  const handleApplyStock = async (url: string) => {
    const tgt = stockTarget;
    setStockTarget(null);
    if (!tgt || !active?.uid || !pieceId || pieceId === "local-map") return;
    try {
      if (tgt === "cover") {
        await updatePieceMeta({ piece_id: pieceId, uid: active.uid, cover: url });
        dispatch(actions.setPieceCover(url));
        dispatch(recordEditHistory("change_cover"));
      } else {
        await updatePieceMeta({ piece_id: pieceId, uid: active.uid, background: url });
        dispatch(actions.setPieceBackground(url));
        dispatch(recordEditHistory("change_background"));
        triggerReload(); // 編集シーンを再構築して背景を反映
      }
      toast.success(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_TOAST_EDIT_SUCCESS")}</p>
        </div>,
      );
    } catch {
      toast.warn(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
        </div>,
      );
    }
  };

  const handleChangeHint = (level: number, value: string) => {
    setEditHints((prev) => {
      const next = [...prev];
      next[level] = value;
      return next;
    });
  };

  const handleEdit = (field: EditField) => () => {
    play();
    btnDelay(() => {
      if (field === EditField.NAME) setEditName(name);
      else if (field === EditField.DESC) setEditDesc(description);
      else setEditHints([hint[0] ?? "", hint[1] ?? "", hint[2] ?? ""]);
      setEditing(field);
    });
  };

  const handleCancelEdit = () => {
    play();
    btnDelay(() => setEditing(null));
  };

  const handleConfirmEdit = () => {
    if (editing === EditField.DESC && descRemaining > 0) return;
    play();
    btnDelay(async () => {
      // 変えたものは記録に残す（同じ内容で決定したときは残さない）
      if (editing === EditField.NAME) {
        dispatch(actions.setPieceName(editName));
        await updatePieceMeta({ piece_id: pieceId, uid: active.uid, name: editName, description });
        if (editName !== name) dispatch(recordEditHistory("rename"));
      } else if (editing === EditField.DESC) {
        dispatch(actions.setPieceDescription(editDesc));
        await updatePieceMeta({ piece_id: pieceId, uid: active.uid, name, description: editDesc });
        if (editDesc !== description) dispatch(recordEditHistory("change_description"));
      } else if (editing === EditField.HINTS) {
        editHints.forEach((value, level) => dispatch(actions.setPieceHint({ level, value })));
        await updatePieceHints({ piece_id: pieceId, uid: active.uid, hints: editHints });
        if (editHints.some((value, level) => value !== (hint[level] ?? ""))) {
          dispatch(recordEditHistory("change_hints"));
        }
      }
      toast.success(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_TOAST_EDIT_SUCCESS")}</p>
        </div>,
      );
      setEditing(null);
    });
  };

  const editTitle =
    editing === EditField.NAME
      ? I18n.t("MSG_BUILDER_META_NAME_LABEL")
      : editing === EditField.DESC
        ? I18n.t("MSG_BUILDER_META_DESC_LABEL")
        : I18n.t("MSG_BUILDER_DETAIL_HINT_SECTION");

  return (
    <>
      <InScreenModal open={open}>
        <BuilderModalPanel
          accent={BuilderModalAccent.GREEN}
          icon="info"
          title={I18n.t("MSG_BUILDER_META_DETAIL_BTN")}
          width={1040}
          onClose={handleBack}
          onHelp={() => setShowHelp(true)}
        >
          {/* 中央：左（画像）＋ 右（タブ＋パネル） */}
          <div className="flex-row-el w-full gap-4 h-[400px] min-h-0">
            <div className="flex-col-el w-[360px] shrink-0 gap-3">
              <div className="flex-col-el w-full aspect-[4/3] rounded-2xl overflow-hidden bg-beige-60 border-solid border-4 border-green-100 shadow-md">
                <img
                  alt={name}
                  draggable={false}
                  src={cover ?? editingMap?.capture ?? FALLBACK_IMAGE}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
                  }}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* カバー / はいけい変更（持ち物から選ぶ） */}
              <div data-guide="info_cover" className="flex-col-el w-full gap-3 pb-1">
                <BuilderPillButton
                  color={PillColor.GREEN}
                  size={PillSize.SM}
                  icon="image"
                  label={I18n.t("MSG_BUILDER_DETAIL_CHANGE_COVER")}
                  className="!w-full"
                  onClick={handleChangeCover}
                />
                <BuilderPillButton
                  color={PillColor.BLUE}
                  size={PillSize.SM}
                  icon="wallpaper"
                  label={I18n.t("MSG_BUILDER_DETAIL_CHANGE_BACKGROUND")}
                  className="!w-full"
                  onClick={handleChangeBackground}
                />
              </div>
            </div>

            <div className="flex-col-el flex-1 min-w-0 min-h-0 overflow-hidden bg-white rounded-2xl border-solid border-2 border-gray2-60 shadow-md">
              {/* タブバー */}
              <div
                data-guide="info_tabs"
                className="flex-row-el items-center shrink-0 box-border w-full gap-2 bg-beige-60 p-2 rounded-t-2xl"
              >
                {TAB_ITEMS.map(({ id, labelKey, icon }) => {
                  const active2 = id === tab;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={handleSelectTab(id)}
                      className={`type-inherit-font flex-row-center flex-1 min-w-0 gap-1 h-12 px-2 rounded-xl border-solid border-2 cursor-pointer transition-colors ${
                        active2
                          ? "bg-green-100 border-green-100 text-white"
                          : "border-white bg-white text-textcolor-black/70"
                      }`}
                    >
                      <span className="material-symbols-outlined type-md shrink-0">{icon}</span>
                      <p className="type-text type-sm min-w-0 truncate whitespace-nowrap text-center">
                        {I18n.t(labelKey)}
                      </p>
                    </button>
                  );
                })}
              </div>

              {/* パネル */}
              <div className="flex-col-el items-start flex-1 min-h-0 box-border w-full gap-2 bg-white p-4 overflow-y-auto invisible-scrollbar">
                {tab === DetailTab.INFO && (
                  <>
                    {/* なまえ（読み取り表示＋へんこう） */}
                    <div data-guide="info_name" className="flex-col-el items-start w-full gap-1">
                      <p className="type-text-left type-sm text-textcolor-black/70 w-full">
                        {I18n.t("MSG_BUILDER_META_NAME_LABEL")}
                      </p>
                      <div className="flex-row-el items-center w-full gap-2">
                        <p
                          className={`type-text-left type-body flex-1 min-w-0 truncate ${
                            name ? "text-textcolor-black" : "text-textcolor-black/40"
                          }`}
                        >
                          {name || I18n.t("MSG_BUILDER_MAP_NAME_PLACEHOLDER")}
                        </p>
                        <div className="shrink-0 pb-1">
                          <BuilderPillButton
                            color={PillColor.GREEN}
                            size={PillSize.SM}
                            icon="edit"
                            label={I18n.t("MSG_BUILDER_DETAIL_EDIT_BTN")}
                            onClick={handleEdit(EditField.NAME)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* せつめい（読み取り表示＋へんこう） */}
                    <div className="flex-col-el items-start w-full gap-1">
                      <p className="type-text-left type-sm text-textcolor-black/70 w-full">
                        {I18n.t("MSG_BUILDER_META_DESC_LABEL")}
                      </p>
                      <p
                        className={`type-text-left type-body w-full whitespace-pre-wrap ${
                          description ? "text-textcolor-black" : "text-textcolor-black/40"
                        }`}
                      >
                        {description || I18n.t("MSG_BUILDER_META_DESC_PLACEHOLDER")}
                      </p>
                      <div className="flex-row-el justify-end w-full pb-1">
                        <BuilderPillButton
                          color={PillColor.GREEN}
                          size={PillSize.SM}
                          icon="edit"
                          label={I18n.t("MSG_BUILDER_DETAIL_EDIT_BTN")}
                          onClick={handleEdit(EditField.DESC)}
                        />
                      </div>
                    </div>

                    {/* 統計（ドラフトは 0） */}
                    <div className="flex-row-el flex-wrap items-center gap-4 w-full rounded-xl p-3 bg-green-100/20">
                      <div className="flex-row-el items-center gap-1">
                        <span className="material-symbols-outlined text-blue-100 type-body">
                          visibility
                        </span>
                        <p className="type-text type-body text-textcolor-black">0</p>
                      </div>
                      <div className="flex-row-el items-center gap-1">
                        <span className="material-symbols-outlined material-symbols-filled text-red-100 type-body">
                          favorite
                        </span>
                        <p className="type-text type-body text-textcolor-black">0</p>
                      </div>
                      <div className="flex-row-el items-center gap-1">
                        <span className="material-symbols-outlined text-green-100 type-body">
                          play_arrow
                        </span>
                        <p className="type-text type-body text-textcolor-black">0</p>
                      </div>
                      <div className="flex-row-el items-center gap-1">
                        <span className="material-symbols-outlined text-green-100 type-body">
                          military_tech
                        </span>
                        <p className="type-text type-body text-textcolor-black">0</p>
                      </div>
                      <div className="flex-row-el items-center gap-1">
                        <span className="material-symbols-outlined text-purple type-body">
                          cached
                        </span>
                        <p className="type-text type-body text-textcolor-black">0</p>
                      </div>
                    </div>

                    {/* リアクション（ドラフトは 0） */}
                    <div className="flex-row-el flex-wrap items-center gap-4 w-full rounded-xl p-3 bg-green-100/20">
                      {REACTIONS.map(({ key, icon }) => (
                        <div key={key} className="flex-row-el items-center gap-1">
                          <div className="flex-col-center w-10 h-10">{icon}</div>
                          <p className="type-text type-body text-textcolor-black">0</p>
                        </div>
                      ))}
                    </div>

                    {/* 公開日時 */}
                    <div className="flex-row-el flex-wrap items-center gap-x-4 gap-y-1 w-full rounded-xl p-3 bg-green-100/20">
                      <div className="flex-row-el items-center gap-1">
                        <span className="material-symbols-outlined text-blue-100 type-body">
                          public
                        </span>
                        <p className="type-text type-body text-textcolor-black">
                          {I18n.t("MSG_BUILDER_DETAIL_PUBLISHED_AT")}
                        </p>
                        <p className="type-text type-body text-textcolor-black">-</p>
                      </div>
                      <div className="flex-row-el items-center gap-1">
                        <span className="material-symbols-outlined text-green-100 type-body">
                          update
                        </span>
                        <p className="type-text type-body text-textcolor-black">
                          {I18n.t("MSG_BUILDER_DETAIL_UPDATED_AT")}
                        </p>
                        <p className="type-text type-body text-textcolor-black">
                          {dayjs().format("YYYY/MM/DD")}
                        </p>
                      </div>
                    </div>

                    {/* ヒント（読み取り表示＋へんこう） */}
                    <p className="type-text-left type-md text-textcolor-black w-full">
                      {I18n.t("MSG_BUILDER_DETAIL_HINT_SECTION")}
                    </p>
                    <div className="flex-col-el w-full gap-2">
                      {HINT_LEVELS.map((lv, i) => (
                        <div
                          key={lv}
                          className="flex-col-el items-start w-full rounded-xl p-3 gap-1 bg-green-100/20"
                        >
                          <p className="type-text-left type-body text-textcolor-black/70 w-full">
                            {I18n.t(lv)}
                          </p>
                          <p
                            className={`type-text-left type-body w-full ${
                              hint[i] ? "text-textcolor-black" : "text-textcolor-black/40"
                            }`}
                          >
                            {hint[i] || "—"}
                          </p>
                        </div>
                      ))}
                      <div className="flex-row-el justify-end w-full pb-1">
                        <BuilderPillButton
                          color={PillColor.GREEN}
                          size={PillSize.SM}
                          icon="edit"
                          label={I18n.t("MSG_BUILDER_DETAIL_EDIT_BTN")}
                          onClick={handleEdit(EditField.HINTS)}
                        />
                      </div>
                    </div>
                  </>
                )}

                {tab === DetailTab.COMMAND && (
                  <MapDetailCommandPanel
                    enabledCommands={new Set(enabledCommands)}
                    commandLimits={commandLimits}
                  />
                )}

                {tab === DetailTab.RANKING && (
                  <div className="flex-col-el w-full gap-2">
                    {ranking.length === 0 ? (
                      <p className="type-text type-sm text-textcolor-black/70">
                        {I18n.t("MSG_BUILDER_DETAIL_RANKING_EMPTY")}
                      </p>
                    ) : (
                      ranking.map((r) => (
                        <div
                          key={r.uid}
                          className="flex-row-el items-center gap-2 w-full rounded-xl px-3 py-2 bg-beige-60"
                        >
                          <p className="type-text type-sm">{RANK_MEDALS[r.rank - 1] ?? ""}</p>
                          <p className="type-text type-sm text-textcolor-black w-6 text-center">
                            {r.rank}
                          </p>
                          <div className="flex-col-center shrink-0">
                            <UserAvatar size={32} icon={r.icon ?? ""} />
                          </div>
                          <p className="type-text type-sm text-textcolor-black flex-1 text-left truncate">
                            {r.name ?? ""}
                          </p>
                          <p className="type-text type-sm text-textcolor-black whitespace-nowrap">
                            {I18n.t("MSG_BUILDER_DETAIL_RANKING_MOVES_UNIT", { n: r.moves })}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* コメント（閲覧のみ・投稿欄なし） */}
                {tab === DetailTab.COMMENT && (
                  <div className="flex-col-el w-full gap-2">
                    {comments.length === 0 ? (
                      <p className="type-text type-sm text-textcolor-black/70">
                        {I18n.t("MSG_BUILDER_DETAIL_COMMENT_EMPTY")}
                      </p>
                    ) : (
                      comments.map((c) => (
                        <div
                          key={c.id}
                          className="flex-row-el items-start gap-2 w-full rounded-xl p-3 bg-beige-60"
                        >
                          <div className="flex-col-center shrink-0">
                            <UserAvatar size={32} icon={c.author_icon ?? ""} />
                          </div>
                          <div className="flex-col-el items-start flex-1 min-w-0 gap-1">
                            <p className="type-text-left type-sm text-textcolor-black/70">
                              {c.author_name ?? ""}
                            </p>
                            <p className="type-text-left type-body text-textcolor-black w-full">
                              {c.content}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </BuilderModalPanel>
      </InScreenModal>

      {/* 項目別の編集Modal */}
      <InScreenModal open={editing !== null} onBackdropClose={handleCancelEdit}>
        <BuilderModalPanel
          accent={BuilderModalAccent.GREEN}
          icon="edit"
          title={editTitle}
          width={520}
          onClose={handleCancelEdit}
        >
          {editing === EditField.NAME && (
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder={I18n.t("MSG_BUILDER_MAP_NAME_PLACEHOLDER")}
              maxLength={20}
              className="input-modal type-body text-textcolor-black w-full h-11 px-3"
            />
          )}

          {editing === EditField.DESC && (
            <div className="flex-col-el items-start w-full gap-1">
              <div className="flex-row-el items-center justify-end w-full">
                {descRemaining > 0 ? (
                  <p className="type-text type-sm text-orange-100">
                    {I18n.t("MSG_BUILDER_META_DESC_MIN", { count: descRemaining })}
                  </p>
                ) : (
                  <p className="type-text type-sm text-green-100">✓</p>
                )}
              </div>
              <textarea
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                placeholder={I18n.t("MSG_BUILDER_META_DESC_PLACEHOLDER")}
                maxLength={512}
                rows={4}
                className="input-modal type-body text-textcolor-black w-full p-3 resize-none"
              />
            </div>
          )}

          {editing === EditField.HINTS && (
            <div className="flex-col-el w-full gap-2">
              {HINT_LEVELS.map((lv, i) => (
                <div key={lv} className="flex-col-el items-start w-full gap-1">
                  <p className="type-text-left type-sm text-textcolor-black/70 w-full">
                    {I18n.t(lv)}
                  </p>
                  <input
                    type="text"
                    value={editHints[i] ?? ""}
                    onChange={(e) => handleChangeHint(i, e.target.value)}
                    placeholder={I18n.t("MSG_BUILDER_DETAIL_HINT_PLACEHOLDER")}
                    maxLength={100}
                    className="input-modal type-body text-textcolor-black w-full h-11 px-3"
                  />
                </div>
              ))}
            </div>
          )}

          {/* フッター：やめる / かんりょう */}
          <div className="flex-row-el flex-center w-full gap-4 pb-2">
            <BuilderPillButton
              color={PillColor.BLUE}
              size={PillSize.MD}
              icon="close"
              label={I18n.t("MSG_STOP_BTN")}
              onClick={handleCancelEdit}
            />
            <BuilderPillButton
              color={PillColor.GREEN}
              size={PillSize.MD}
              icon="check"
              label={I18n.t("MSG_CONFIRM_BTN")}
              disabled={editing === EditField.DESC && descRemaining > 0}
              onClick={handleConfirmEdit}
            />
          </div>
        </BuilderModalPanel>
      </InScreenModal>

      <BuilderHelpPopup open={showHelp} onClose={() => setShowHelp(false)} chapters={HELP_DETAIL} />

      {/* カバー / はいけい を持ち物から選ぶ Modal（cover/background 共用） */}
      <StockSelectModal
        open={stockTarget !== null}
        target={stockTarget ?? "cover"}
        currentUrl={stockTarget === "cover" ? cover : background}
        onSelect={handleApplyStock}
        onClose={() => setStockTarget(null)}
      />
    </>
  );
};
